import { useAction, useAtom } from '@reatom/npm-react'
import styled from 'styled-components'

import { CircularLoader, useHapticFeedback } from '@blade/ui-kit'
import { SettingsCheckbox } from '@root/features/settings/ui/settings-checkbox'
import {
  languageInfosAtom,
  languageSelectionAtom,
  systemLanguageInfoAtom,
  useTranslation,
} from '@root/shared/localization'

import {
  changeLanguageAction,
  changeLanguageStateAtom,
} from '../atoms/languages'
import * as ChangeLanguageState from '../model/change-language-state'

export const SettingsLanguagesList: React.FC = () => {
  const changeLanguage = useAction(changeLanguageAction)
  const [languages] = useAtom(languageInfosAtom)
  const [languageSelection] = useAtom(languageSelectionAtom)
  const [changeLanguageState] = useAtom(changeLanguageStateAtom)
  const [systemLanguage] = useAtom(systemLanguageInfoAtom)
  const haptics = useHapticFeedback()
  const { t } = useTranslation()

  const isChecked = (lang: string) =>
    languageSelection.type === 'system'
      ? false
      : languageSelection.lang === lang

  const renderSystemButton = () => {
    if (systemLanguage) {
      return (
        <Section>
          <Item
            onClick={() => {
              haptics.impactOccurred('light')
              changeLanguage(undefined)
            }}
          >
            <Group>
              <Title aria-checked={languageSelection.type === 'system'}>
                {t('settings.language.auto-description')}
              </Title>
              <Title>{systemLanguage.localName}</Title>
            </Group>
            <SettingsCheckbox
              checked={languageSelection.type === 'system'}
              radius="full"
            />
          </Item>
        </Section>
      )
    }
  }

  return (
    <Root>
      {renderSystemButton()}
      <Section>
        {languages.map((it) => (
          <Item
            key={it.lang}
            onClick={() => {
              haptics.impactOccurred('light')

              changeLanguage(it.lang)
            }}
          >
            <Group>
              <Title aria-checked={isChecked(it.lang)}>{it.name}</Title>
              <Title>{it.localName}</Title>
            </Group>
            <Container>
              {ChangeLanguageState.match(changeLanguageState, {
                ChangeLanguageStateDone: () => (
                  <SettingsCheckbox
                    checked={isChecked(it.lang)}
                    radius="full"
                  />
                ),
                ChangeLanguageStateLoading: ({ lang }) =>
                  lang === it.lang ? (
                    <CircularLoader size={16} />
                  ) : (
                    <SettingsCheckbox
                      checked={isChecked(it.lang)}
                      radius="full"
                    />
                  ),
              })}
            </Container>
          </Item>
        ))}
      </Section>
    </Root>
  )
}

const Root = styled.div`
  display: grid;
  gap: 12px;
`

const Section = styled.div`
  display: grid;
`

const Item = styled.div`
  display: grid;
  position: relative;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  cursor: pointer;
  background: var(--background-2);

  height: 44px;
  padding-inline: 20px;

  gap: 8px;
  &:nth-child(even) {
    background: rgba(var(--background-2-rgb), 0.5);
  }
  &:hover {
    background: var(--backgroundA-3);
  }

  &:first-child {
    border-top-left-radius: 12px;
    border-top-right-radius: 12px;
  }

  &:last-child {
    border-bottom-left-radius: 12px;
    border-bottom-right-radius: 12px;
  }

  &:not(:last-child) {
    &:after {
      content: '';
      display: block;

      height: 1px;
      position: absolute;
      bottom: 0;
      left: 12px;
      right: 12px;
      background: var(--backgroundA-3);
    }
  }
`

const Group = styled.div`
  display: grid;
  grid-template-columns: 1fr 100px;
  gap: 4px;
`

const Title = styled.div`
  color: var(--text-9);

  font-size: 12px;
  font-weight: 500;
  line-height: 16px;

  text-align: left;
  align-self: start;

  transition: color 0.1s ease-in-out;

  &[aria-checked='true'] {
    color: var(--text-12);
  }
  ${Item}:hover &[aria-checked='false'] {
    color: var(--text-10);
  }
`

const Container = styled.div``
